import React, { useState, useEffect } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { ROLE_LABELS } from '../context/AuthContext';
import {
  HiOutlinePlus,
  HiOutlineSearch,
  HiOutlineUserGroup,
  HiOutlineX,
  HiOutlineShieldCheck,
  HiOutlineTrash,
} from 'react-icons/hi';
import './Users.css';

const ROLES = ['admin', 'manager', 'salesman', 'warehouse', 'delivery', 'cashier', 'accountant'];

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({
    username: '', email: '', password: '', full_name: '', role: 'salesman', commission_percentage: '5',
  });
  const [saving, setSaving] = useState(false);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await api.get('/auth/users');
      setUsers(res.data?.data || []);
    } catch {
      toast.error('Error cargando usuarios');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const filtered = users.filter((u) => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (
      u.full_name?.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q) ||
      u.username?.toLowerCase().includes(q) ||
      u.role?.toLowerCase().includes(q)
    );
  });

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.username || !form.email || !form.password) {
      toast.error('Username, email y contraseña son requeridos');
      return;
    }
    if (form.password.length < 8) {
      toast.error('La contraseña debe tener mínimo 8 caracteres');
      return;
    }
    setSaving(true);
    try {
      await api.post('/auth/register', form);
      toast.success('Usuario creado');
      setShowModal(false);
      setForm({ username: '', email: '', password: '', full_name: '', role: 'salesman', commission_percentage: '5' });
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error creando usuario');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async (user) => {
    if (!window.confirm(`¿Eliminar al usuario "${user.full_name || user.username}"?\n\nEmail: ${user.email}\nRol: ${ROLE_LABELS[user.role] || user.role}\n\nEsta acción desactivará su cuenta.`)) return;
    try {
      await api.delete(`/auth/users/${user.id}`);
      toast.success(`Usuario "${user.full_name || user.username}" eliminado`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error eliminando usuario');
    }
  };

  return (
    <div className="users-page">
      <div className="page-header">
        <div>
          <h1>Gestión de Usuarios</h1>
          <p>{users.length} usuarios en el sistema</p>
        </div>
        <button className="btn-primary" onClick={() => setShowModal(true)}>
          <HiOutlinePlus /> Nuevo Usuario
        </button>
      </div>

      <div className="filters-bar">
        <div className="search-box">
          <HiOutlineSearch className="search-icon" />
          <input
            type="text"
            placeholder="Buscar por nombre, email, rol..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div className="loading-state"><div className="loader"></div><p>Cargando usuarios...</p></div>
      ) : filtered.length === 0 ? (
        <div className="empty-state-card">
          <HiOutlineUserGroup className="empty-icon" />
          <p>No se encontraron usuarios</p>
        </div>
      ) : (
        <div className="users-grid">
          {filtered.map((user) => (
            <div key={user.id} className="user-card">
              <div className="user-card-avatar">
                {user.full_name?.charAt(0)?.toUpperCase() || user.username?.charAt(0)?.toUpperCase() || 'U'}
              </div>
              <div className="user-card-info">
                <h4>{user.full_name || user.username}</h4>
                <span className="user-card-email">{user.email}</span>
                <div className="user-card-badges">
                  <span className={`role-badge role-${user.role}`}>
                    <HiOutlineShieldCheck /> {ROLE_LABELS[user.role] || user.role}
                  </span>
                </div>
              </div>
              <button
                className="btn-delete-card"
                onClick={() => handleDeleteUser(user)}
                title="Eliminar usuario"
              >
                <HiOutlineTrash />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Modal crear */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Nuevo Usuario</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}><HiOutlineX /></button>
            </div>
            <form onSubmit={handleCreate} className="modal-form">
              <div className="form-grid-2">
                <div className="form-group">
                  <label>Nombre Completo</label>
                  <input
                    type="text"
                    value={form.full_name}
                    onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                    placeholder="Juan Pérez"
                  />
                </div>
                <div className="form-group">
                  <label>Username *</label>
                  <input
                    type="text"
                    value={form.username}
                    onChange={(e) => setForm({ ...form, username: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Contraseña * (mín. 8 caracteres)</label>
                <input
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  required
                  minLength={8}
                />
              </div>
              <div className="form-group">
                <label>Rol</label>
                <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                  {ROLES.map((role) => (
                    <option key={role} value={role}>{ROLE_LABELS[role]}</option>
                  ))}
                </select>
              </div>
              {form.role === 'salesman' && (
                <div className="form-group">
                  <label>Comisión (%)</label>
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    max="100"
                    value={form.commission_percentage}
                    onChange={(e) => setForm({ ...form, commission_percentage: e.target.value })}
                    placeholder="5.00"
                  />
                </div>
              )}
              <button type="submit" className="btn-primary full" disabled={saving}>
                {saving ? 'Creando...' : 'Crear Usuario'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
